import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Types based on the actual API response structure - matching the existing ApplicationData type
interface ApplicationData extends Record<string, unknown> {
	id: number;
	uuid: string;
	AppId: string;
	instituteId: string;
	name: string;
	headName: string;
	activity: string;
	submissionCount: number;
	appliedOn: string;
	status: string;
	technicalCoursesQuantity: number;
	vocationalCoursesQuantity: number;
	applicationType: string;
	createdAt: string;
	// Additional fields from the detailed response
	description?: string | null;
	landlineNumber?: string;
	whatsappNumber?: string;
	phoneNumber?: string;
	establishmentDate?: string;
	ownerCNIC?: string;
	emailAddress?: string;
	geoLocation?: string;
	latitude?: number;
	longitude?: number;
	ownerName?: string;
	ownerPhoneNumber?: string;
	ownerAddress?: string | null;
	ownerLandlineNumber?: string;
	principleName?: string;
	principlePhoneNumber?: string;
	principleAddress?: string | null;
	principleLandlineNumber?: string;
	principleEmailAddress?: string | null;
	coveredArea?: string;
	totalArea?: string;
	totalFee?: string;
	paidFee?: string;
	rejectedReason?: string | null;
	rejectedAt?: string | null;
	currentStep?: number;
	updatedAt?: string;
	institute?: {
		id: number;
		uuid: string;
		createdAt: string;
		updatedAt: string;
		name: string;
		status: string;
		establishmentDate: string;
		signupDate: string;
		headName: string | null;
		vocationalCoursesQuantity: number | null;
		technicalCoursesQuantity: number | null;
	};
	applicationAttachments?: Array<{
		id: number;
		uuid: string;
		createdAt: string;
		updatedAt: string;
		remarks: string | null;
		attachmentType?: {
			id: number;
			uuid: string;
			createdAt: string;
			updatedAt: string;
			name: string;
			isRequired: boolean;
			allowMultiple: boolean;
			isActive: boolean;
			sortOrder: number;
		};
		attachmentTypeOption?: {
			id: number;
			uuid: string;
			createdAt: string;
			updatedAt: string;
			fieldName: string;
			fieldLabel: string;
		};
		files?: Array<{
			id: number;
			uuid: string;
			createdAt: string;
			updatedAt: string;
			filePath: string;
			fileSize: number | null;
		}>;
	}>;
	applicationCourses?: Array<{
		id: number;
		uuid: string;
		createdAt: string;
		updatedAt: string;
		course?: {
			id: number;
			uuid: string;
			name: string;
			description?: string;
		};
	}>;
	applicationFacilities?: Array<{
		id: number;
		uuid: string;
		createdAt: string;
		updatedAt: string;
		instituteFacility: {
			id: number;
			uuid: string;
			createdAt: string;
			updatedAt: string;
			name: string;
			isRequired: boolean;
			sortOrder: number;
			type: string;
			isActive: boolean;
		};
	}>;
	challan?: {
		id: number;
		uuid: string;
		createdAt: string;
		updatedAt: string;
		applicationProcessingFee: string;
		challanProcessingFee: string;
		technicalCoursesFee: string;
		vocationalCoursesFee: string;
		totalAmount: string;
		additionalCoursesFee: string;
		psid: string;
		expiredAt: string;
		challanStatus: string;
		applicationPayment: {
			id: number;
			uuid: string;
			createdAt: string;
			updatedAt: string;
			amount: string;
			status: string;
			paymentMethod: string;
		} | null;
	};
}

interface ApplicationsState {
	applications: ApplicationData[];
	currentApplication: ApplicationData | null;
	isLoading: boolean;
	isError: boolean;
	error: string | null;
}

const initialState: ApplicationsState = {
	applications: [],
	currentApplication: null,
	isLoading: false,
	isError: false,
	error: null,
};

const applicationsSlice = createSlice({
	name: 'applications',
	initialState,
	reducers: {
		setApplications: (state, action: PayloadAction<ApplicationData[]>) => {
			state.applications = action.payload;
			state.isLoading = false;
			state.isError = false;
			state.error = null;
		},
		setCurrentApplication: (state, action: PayloadAction<ApplicationData>) => {
			state.currentApplication = action.payload;
		},
		setApplicationsLoading: (state, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload;
		},
		setApplicationsError: (state, action: PayloadAction<string>) => {
			state.isError = true;
			state.error = action.payload;
			state.isLoading = false;
		},
		clearApplicationsError: (state) => {
			state.isError = false;
			state.error = null;
		},
		clearApplications: (state) => {
			state.applications = [];
			state.currentApplication = null;
			state.isLoading = false;
			state.isError = false;
			state.error = null;
		},
	},
});

export const { 
	setApplications, 
	setCurrentApplication, 
	setApplicationsLoading, 
	setApplicationsError, 
	clearApplicationsError, 
	clearApplications 
} = applicationsSlice.actions;

export default applicationsSlice.reducer; 